// CTAP2 Credential Management Protocol Implementation
// Implements authenticatorCredentialManagement (0x0A) commands

import {encodeCBOR, parseCBOR, hexStringToByte} from './util';
import {getPinTokenWithPermissions, PIN_PERMISSIONS} from './ctap2-pin-protocol';
import {calculatePinUvAuthParam} from './ctap2-crypto';

// Helper to build CBOR map with raw byte values
function buildCBORMapWithRawValue(entries) {
  // entries: [{key: number, value: number | Uint8Array | 'raw'}]
  // If value is 'raw', the next entry's value should be the raw bytes
  
  const result = [];
  const mapSize = entries.filter(e => e.value !== 'raw').length;
  
  // Map header
  if (mapSize < 24) {
    result.push(0xA0 | mapSize);
  } else {
    result.push(0xB8);
    result.push(mapSize);
  }
  
  for (let i = 0; i < entries.length; i++) {
    const {key, value} = entries[i];
    
    // Encode key (always a small positive integer)
    if (key < 24) {
      result.push(key);
    } else {
      result.push(0x18);
      result.push(key);
    }
    
    // Encode value
    if (value === 'raw') {
      // Next entry contains raw bytes
      i++;
      const rawBytes = entries[i].value;
      result.push(...rawBytes);
    } else if (typeof value === 'number') {
      if (value < 24) {
        result.push(value);
      } else {
        result.push(0x18);
        result.push(value);
      }
    } else if (value instanceof Uint8Array) {
      // Byte string
      if (value.length < 24) {
        result.push(0x40 | value.length);
      } else if (value.length < 256) {
        result.push(0x58);
        result.push(value.length);
      } else {
        result.push(0x59);
        result.push((value.length >> 8) & 0xFF);
        result.push(value.length & 0xFF);
      }
      result.push(...value);
    }
  }
  
  return new Uint8Array(result);
}

// Credential Management subcommands
const CRED_MGMT_SUBCOMMAND = {
  GET_CREDS_METADATA: 0x01,
  ENUMERATE_RPS_BEGIN: 0x02,
  ENUMERATE_RPS_GET_NEXT: 0x03,
  ENUMERATE_CREDENTIALS_BEGIN: 0x04,
  ENUMERATE_CREDENTIALS_GET_NEXT: 0x05,
  DELETE_CREDENTIAL: 0x06,
  UPDATE_USER_INFORMATION: 0x07
};

// ==================== Get Credentials Metadata ====================

/**
 * Get credentials metadata (count)
 * @param {Function} transceive - APDU transceive function
 * @param {string} pin - User's PIN
 * @returns {Object} {existingCount, maxRemaining}
 */
export async function getCredsMetadata(transceive, pin) {
  // 1. Get PIN token with CM permission (no RP ID)
  const pinToken = await getPinTokenWithPermissions(transceive, pin, PIN_PERMISSIONS.CREDENTIAL_MANAGEMENT);
  
  // 2. GET_CREDS_METADATA has no subCommandParams
  // Device verifies HMAC(pinToken, [subCommand])
  const subCmdByte = new Uint8Array([CRED_MGMT_SUBCOMMAND.GET_CREDS_METADATA]);
  
  // 3. Calculate pinUvAuthParam: HMAC(pinToken, [subCommand])
  const pinUvAuthParam = await calculatePinUvAuthParam(pinToken, subCmdByte);
  
  // 4. Build CBOR request
  // Keys: 1=subCommand, 3=pinUvAuthProtocol, 4=pinUvAuthParam
  const cbor = encodeCBOR({
    1: CRED_MGMT_SUBCOMMAND.GET_CREDS_METADATA,  // subCommand (CM_REQ_SUB_COMMAND = 0x01)
    3: 1,  // pinUvAuthProtocol (CM_REQ_PIN_UV_AUTH_PROTOCOL = 0x03)
    4: pinUvAuthParam  // pinUvAuthParam (CM_REQ_PIN_UV_AUTH_PARAM = 0x04)
  });
  
  // 5. Build APDU
  const cborHex = Array.from(cbor).map(b => b.toString(16).padStart(2, '0')).join('');
  const len = (cbor.length + 1).toString(16).padStart(2, '0');
  const apdu = `80100000${len}0A${cborHex}00`;
  
  const response = await transceive(apdu);
  
  if (response.slice(-4) !== '9000') {
    throw new Error(`APDU error: ${response.slice(-4)}`);
  }
  
  // Parse response
  const respBytes = hexStringToByte(response.slice(0, -4));
  if (respBytes[0] !== 0x00) {
    throw new Error(`CTAP error: 0x${respBytes[0].toString(16)}`);
  }
  
  const result = parseCBOR(respBytes.slice(1));
  return {
    existingCount: result[1] || 0,
    maxRemaining: result[2] || 0
  };
}

// ==================== Enumerate RPs ====================

/**
 * Enumerate all Relying Parties
 * @param {Function} transceive - APDU transceive function
 * @param {string} pin - User's PIN
 * @returns {Array} List of RPs with {rp, rpIDHash, totalCredentials}
 */
export async function enumerateRPs(transceive, pin) {
  try {
    console.log('enumerateRPs: Starting...');
    // 1. Get PIN token with CM permission (no RP ID)
    const pinToken = await getPinTokenWithPermissions(transceive, pin, PIN_PERMISSIONS.CREDENTIAL_MANAGEMENT);
    console.log('enumerateRPs: Got PIN token, length:', pinToken.length);
    
    // 2. ENUMERATE_RPS_BEGIN has no subCommandParams
    // Device verifies HMAC(pinToken, [subCommand])
    const subCmdByte = new Uint8Array([CRED_MGMT_SUBCOMMAND.ENUMERATE_RPS_BEGIN]);
    console.log('enumerateRPs: Calculating HMAC for subCommand:', subCmdByte[0]);
  
  // 3. Calculate pinUvAuthParam: HMAC(pinToken, [subCommand])
  const pinUvAuthParam = await calculatePinUvAuthParam(pinToken, subCmdByte);
  
  // 4. Build CBOR request for BEGIN
  // Keys: 1=subCommand, 3=pinUvAuthProtocol, 4=pinUvAuthParam
  const cbor = encodeCBOR({
    1: CRED_MGMT_SUBCOMMAND.ENUMERATE_RPS_BEGIN,  // subCommand (CM_REQ_SUB_COMMAND = 0x01)
    3: 1,  // pinUvAuthProtocol (CM_REQ_PIN_UV_AUTH_PROTOCOL = 0x03)
    4: pinUvAuthParam  // pinUvAuthParam (CM_REQ_PIN_UV_AUTH_PARAM = 0x04)
  });
  
  // 5. Build APDU
  const cborHex = Array.from(cbor).map(b => b.toString(16).padStart(2, '0')).join('');
  const len = (cbor.length + 1).toString(16).padStart(2, '0');
  const apdu = `80100000${len}0A${cborHex}00`;
  console.log('enumerateRPs: Sending APDU, length:', apdu.length / 2, 'bytes');
  
  const response = await transceive(apdu);
  console.log('enumerateRPs: Got response:', response);
  
  if (response.slice(-4) !== '9000') {
    throw new Error(`APDU error: ${response.slice(-4)}`);
  }
  
  // Parse response
  const respBytes = hexStringToByte(response.slice(0, -4));
  console.log('enumerateRPs: Response status byte:', respBytes[0].toString(16));
  if (respBytes[0] !== 0x00) {
    // 0x2E = NO_CREDENTIALS
    if (respBytes[0] === 0x2E) {
      console.log('enumerateRPs: No credentials found (0x2E)');
      return [];
    }
    throw new Error(`CTAP error: 0x${respBytes[0].toString(16)}`);
  }
  
  const firstResult = parseCBOR(respBytes.slice(1));
  const rps = [{
    rp: firstResult[3],           // RP entity
    rpIDHash: firstResult[4],     // RP ID hash
    totalCredentials: firstResult[5] || 1
  }];
  
  const totalRPs = firstResult[6] || 1;
  
  // Get remaining RPs
  for (let i = 1; i < totalRPs; i++) {
    const nextCbor = encodeCBOR({
      1: CRED_MGMT_SUBCOMMAND.ENUMERATE_RPS_GET_NEXT
    });
    
    const nextCborHex = Array.from(nextCbor).map(b => b.toString(16).padStart(2, '0')).join('');
    const nextLen = (nextCbor.length + 1).toString(16).padStart(2, '0');
    const nextApdu = `80100000${nextLen}0A${nextCborHex}00`;
    
    const nextResponse = await transceive(nextApdu);
    
    if (nextResponse.slice(-4) !== '9000') {
      console.warn(`Failed to get RP ${i + 1}`);
      break;
    }
    
    const nextRespBytes = hexStringToByte(nextResponse.slice(0, -4));
    if (nextRespBytes[0] !== 0x00) {
      console.warn(`CTAP error for RP ${i + 1}: 0x${nextRespBytes[0].toString(16)}`);
      break;
    }
    
    const nextResult = parseCBOR(nextRespBytes.slice(1));
    rps.push({
      rp: nextResult[3],
      rpIDHash: nextResult[4],
      totalCredentials: nextResult[5] || 1
    });
  }
  
  return rps;
  } catch (error) {
    console.error('enumerateRPs: Error occurred:', error);
    console.error('enumerateRPs: Stack trace:', error.stack);
    throw error;
  }
}

// ==================== Enumerate Credentials ====================

/**
 * Enumerate credentials for a specific RP
 * @param {Function} transceive - APDU transceive function
 * @param {string} pin - User's PIN
 * @param {Uint8Array} rpIDHash - RP ID hash
 * @returns {Array} List of credentials
 */
export async function enumerateCredentials(transceive, pin, rpIDHash) {
  // 1. Get PIN token with CM permission (no RP ID)
  const pinToken = await getPinTokenWithPermissions(transceive, pin, PIN_PERMISSIONS.CREDENTIAL_MANAGEMENT);
  
  // 2. Build subcommand params (raw CBOR map)
  const subCmdParamsCbor = encodeCBOR({
    1: rpIDHash  // CM_PARAM_RP_ID_HASH = 1
  });
  
  // 3. Calculate pinUvAuthParam: HMAC(pinToken, [subCommand] + [raw CBOR of subCommandParams])
  const subCmdByte = new Uint8Array([CRED_MGMT_SUBCOMMAND.ENUMERATE_CREDENTIALS_BEGIN]);
  const combined = new Uint8Array(subCmdByte.length + subCmdParamsCbor.length);
  combined.set(subCmdByte, 0);
  combined.set(subCmdParamsCbor, subCmdByte.length);
  const pinUvAuthParam = await calculatePinUvAuthParam(pinToken, combined);
  
  // 4. Build CBOR request with raw subCommandParams
  const cbor = buildCBORMapWithRawValue([
    {key: 1, value: CRED_MGMT_SUBCOMMAND.ENUMERATE_CREDENTIALS_BEGIN},
    {key: 2, value: 'raw'},  // Mark next value as raw bytes
    {key: 2, value: subCmdParamsCbor},  // Raw CBOR bytes
    {key: 3, value: 1},
    {key: 4, value: pinUvAuthParam}
  ]);
  
  // 5. Build APDU
  const cborHex = Array.from(cbor).map(b => b.toString(16).padStart(2, '0')).join('');
  const len = (cbor.length + 1).toString(16).padStart(2, '0');
  const apdu = `80100000${len}0A${cborHex}00`;
  
  const response = await transceive(apdu);
  
  if (response.slice(-4) !== '9000') {
    throw new Error(`APDU error: ${response.slice(-4)}`);
  }
  
  // Parse response
  const respBytes = hexStringToByte(response.slice(0, -4));
  if (respBytes[0] !== 0x00) {
    if (respBytes[0] === 0x2E) {
      return [];
    }
    throw new Error(`CTAP error: 0x${respBytes[0].toString(16)}`);
  }
  
  const firstResult = parseCBOR(respBytes.slice(1));
  const credentials = [{
    user: firstResult[6],           // User entity
    credentialID: firstResult[7],   // Credential descriptor
    publicKey: firstResult[8],      // Public key (COSE_Key)
    totalCredentials: firstResult[9] || 1,
    credProtect: firstResult[10],   // credProtect level
    largeBlobKey: firstResult[11]   // Large blob key (if present)
  }];
  
  const totalCredentials = firstResult[9] || 1;
  
  // Get remaining credentials
  for (let i = 1; i < totalCredentials; i++) {
    const nextCbor = encodeCBOR({
      1: CRED_MGMT_SUBCOMMAND.ENUMERATE_CREDENTIALS_GET_NEXT
    });
    
    const nextCborHex = Array.from(nextCbor).map(b => b.toString(16).padStart(2, '0')).join('');
    const nextLen = (nextCbor.length + 1).toString(16).padStart(2, '0');
    const nextApdu = `80100000${nextLen}0A${nextCborHex}00`;
    
    const nextResponse = await transceive(nextApdu);
    
    if (nextResponse.slice(-4) !== '9000') {
      console.warn(`Failed to get credential ${i + 1}`);
      break;
    }
    
    const nextRespBytes = hexStringToByte(nextResponse.slice(0, -4));
    if (nextRespBytes[0] !== 0x00) {
      console.warn(`CTAP error for credential ${i + 1}: 0x${nextRespBytes[0].toString(16)}`);
      break;
    }
    
    const nextResult = parseCBOR(nextRespBytes.slice(1));
    credentials.push({
      user: nextResult[6],
      credentialID: nextResult[7],
      publicKey: nextResult[8],
      credProtect: nextResult[10],
      largeBlobKey: nextResult[11]
    });
  }
  
  return credentials;
}

// ==================== Delete Credential ====================

/**
 * Delete a specific credential
 * @param {Function} transceive - APDU transceive function
 * @param {string} pin - User's PIN
 * @param {Object} credentialID - Credential descriptor {type, id}
 */
export async function deleteCredential(transceive, pin, credentialID) {
  // 1. Get PIN token with CM permission (no RP ID)
  const pinToken = await getPinTokenWithPermissions(transceive, pin, PIN_PERMISSIONS.CREDENTIAL_MANAGEMENT);
  
  // 2. Build subcommand params (raw CBOR map)
  // credentialID is from ENUMERATE_CREDENTIALS response (key 7)
  // It should already be a properly formatted credential descriptor with text keys
  // The issue is that our encodeCBOR doesn't support text keys!
  // We need to manually build the credential descriptor CBOR
  
  // Build credential descriptor CBOR: {"id": byte_string, "type": "public-key"}
  const credId = credentialID.id;
  const credType = credentialID.type || "public-key";
  
  console.log('DELETE_CREDENTIAL: credentialID:', credentialID);
  console.log('DELETE_CREDENTIAL: credId type:', typeof credId, 'length:', credId?.length);
  
  // Manual CBOR construction for credential descriptor
  const credDescCbor = [];
  credDescCbor.push(0xA2);  // Map(2)
  
  // Key "id"
  credDescCbor.push(0x62);  // Text string, length 2
  credDescCbor.push(0x69, 0x64);  // "id"
  // Value: byte string
  if (credId.length < 24) {
    credDescCbor.push(0x40 | credId.length);
  } else if (credId.length < 256) {
    credDescCbor.push(0x58, credId.length);
  }
  credDescCbor.push(...credId);
  
  // Key "type"
  credDescCbor.push(0x64);  // Text string, length 4
  credDescCbor.push(0x74, 0x79, 0x70, 0x65);  // "type"
  // Value: "public-key" (text string, length 10)
  credDescCbor.push(0x6A);  // Text string, length 10
  credDescCbor.push(...new TextEncoder().encode(credType));
  
  const credDescCborBytes = new Uint8Array(credDescCbor);
  
  // Now build subCommandParams: {2: <credDescCborBytes>}
  const subCmdParamsCbor = buildCBORMapWithRawValue([
    {key: 2, value: 'raw'},
    {key: 2, value: credDescCborBytes}
  ]);
  
  // 3. Calculate pinUvAuthParam: HMAC(pinToken, [subCommand] + [raw CBOR of subCommandParams])
  const subCmdByte = new Uint8Array([CRED_MGMT_SUBCOMMAND.DELETE_CREDENTIAL]);
  const combined = new Uint8Array(subCmdByte.length + subCmdParamsCbor.length);
  combined.set(subCmdByte, 0);
  combined.set(subCmdParamsCbor, subCmdByte.length);
  const pinUvAuthParam = await calculatePinUvAuthParam(pinToken, combined);
  
  // 4. Build CBOR request with raw subCommandParams
  console.log('DELETE_CREDENTIAL: subCmdParamsCbor:', Array.from(subCmdParamsCbor).map(b => b.toString(16).padStart(2, '0')).join(' '));
  console.log('DELETE_CREDENTIAL: pinUvAuthParam:', Array.from(pinUvAuthParam).map(b => b.toString(16).padStart(2, '0')).join(' '));
  
  const cbor = buildCBORMapWithRawValue([
    {key: 1, value: CRED_MGMT_SUBCOMMAND.DELETE_CREDENTIAL},
    {key: 2, value: 'raw'},  // Mark next value as raw bytes
    {key: 2, value: subCmdParamsCbor},  // Raw CBOR bytes
    {key: 3, value: 1},
    {key: 4, value: pinUvAuthParam}
  ]);
  
  console.log('DELETE_CREDENTIAL: Final CBOR:', Array.from(cbor).map(b => b.toString(16).padStart(2, '0')).join(' '));
  
  // 5. Build APDU
  const cborHex = Array.from(cbor).map(b => b.toString(16).padStart(2, '0')).join('');
  const len = (cbor.length + 1).toString(16).padStart(2, '0');
  const apdu = `80100000${len}0A${cborHex}00`;
  
  const response = await transceive(apdu);
  
  if (response.slice(-4) !== '9000') {
    throw new Error(`APDU error: ${response.slice(-4)}`);
  }
  
  // Parse response
  const respBytes = hexStringToByte(response.slice(0, -4));
  if (respBytes[0] !== 0x00) {
    const errorCodes = {
      0x2E: 'NO_CREDENTIALS - Credential not found',
      0x31: 'PIN_INVALID',
      0x32: 'PIN_AUTH_BLOCKED',
      0x33: 'PIN_REQUIRED'
    };
    const errorName = errorCodes[respBytes[0]] || `0x${respBytes[0].toString(16)}`;
    throw new Error(`CTAP error: ${errorName}`);
  }
  
  return true;
}

// ==================== Get All Credentials (Helper) ====================

/**
 * Get all credentials from all RPs
 * @param {Function} transceive - APDU transceive function
 * @param {string} pin - User's PIN
 * @returns {Array} List of all credentials with RP info
 */
export async function getAllCredentials(transceive, pin) {
  const rps = await enumerateRPs(transceive, pin);
  const allCredentials = [];
  
  for (const rp of rps) {
    const credentials = await enumerateCredentials(transceive, pin, rp.rpIDHash);
    for (const cred of credentials) {
      allCredentials.push({
        ...cred,
        rp: rp.rp,
        rpIDHash: rp.rpIDHash
      });
    }
  }
  
  return allCredentials;
}

